import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import useAddLoanConsultant from 'hooks/loan-consultants/useAddLoanConsultant';
import LoanConsultantsTable from 'components/loan-consultants/LoanConsultantsTable';
import { confirmAlert } from 'react-confirm-alert';
import { useIntl } from 'react-intl';
import 'components/common/AlertOverlay.scss';
import useGetLoanConsultants from 'hooks/loan-consultants/useGetLoanConsultants';
import LoadingOverlay from 'components/common/LoadingOverlay';
import useDeleteLoanConsultant from 'hooks/loan-consultants/useDeleteLoanConsultant';
import type { FormikHelpers } from 'formik';
import type { IAdminRow } from 'components/admins/AdminsTable';
import type { ILoanConsultantFormValues } from 'components/loan-consultants/LoanConsultantsTable';
import { messageOf } from 'utils/error';
import requireValue from 'utils/requireValue';

const useDeleteLoanConsultantHandler = () => {
  const deleteLoanConsultant = useDeleteLoanConsultant();
  const { formatMessage: t } = useIntl();

  const handleDelete = useCallback(
    async ({ email, name }: IAdminRow) => {
      confirmAlert({
        title: t({ id: 'loan_consultants.delete_loan_consultant' }),
        message: t({ id: 'loan_consultants.delete_loan_consultant_message' }, { name }),
        buttons: [
          {
            label: t({ id: 'buttons.cancel' }),
          },
          {
            label: t({ id: 'buttons.delete' }),
            onClick: async () => {
              try {
                // The schema allows a consultant without an email; the backend rejected a null email before (an
                // error toast either way).
                if (email === null) throw new TypeError('The loan consultant has no email address');
                await deleteLoanConsultant({ email });
                toast.success(t({ id: 'loan_consultants.loan_consultant_deleted' }));
              } catch (error) {
                toast.error(messageOf(error));
              }
            },
          },
        ],
      });
    },
    [t, deleteLoanConsultant],
  );
  return handleDelete;
};

const useAddLoanConsultantHandler = () => {
  const addLoanConsultant = useAddLoanConsultant();
  const { formatMessage: t } = useIntl();

  const handleAssign = useCallback(
    async (
      email: ILoanConsultantFormValues,
      { resetForm, setSubmitting }: FormikHelpers<ILoanConsultantFormValues>,
    ) => {
      try {
        await addLoanConsultant(email);
        toast.success(t({ id: 'loan_consultants.loan_consultant_added' }));
        resetForm();
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        setSubmitting(false);
      }
    },
    [addLoanConsultant, t],
  );
  return handleAssign;
};

const LoanConsultantsPage = () => {
  const [loanConsultantsData, loading] = useGetLoanConsultants();
  const deleteLoanConsultant = useDeleteLoanConsultantHandler();
  const addLoanConsultant = useAddLoanConsultantHandler();
  const { formatMessage: t } = useIntl();

  const fields = useMemo(
    () => [
      { key: 'name', label: t({ id: 'loan_consultants.name' }) },
      { key: 'email', label: t({ id: 'loan_consultants.email' }) },
      { key: 'delete', label: ' ' },
    ],
    [t],
  );

  const loanConsultants = useMemo(
    () =>
      // Reading the list, a consultant, its profile or first email throws when it is missing, as it did.
      requireValue(loanConsultantsData, 'map').map((loanConsultant): IAdminRow => {
        const { emails, profile } = requireValue(loanConsultant, 'emails');
        const { firstName, lastName } = requireValue(profile, 'firstName');
        return {
          name: `${firstName} ${lastName}`,
          email: requireValue(requireValue(emails, '0')[0], 'address').address,
        };
      }),
    [loanConsultantsData],
  );

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <LoanConsultantsTable
      onAddLoanConsultant={addLoanConsultant}
      onDeleteLoanConsultant={deleteLoanConsultant}
      {...{ fields, loanConsultants }}
    />
  );
};

export default LoanConsultantsPage;
